import { TranscriptionResult } from '@sos/shared';

export interface LiveTranscriptionHandlers {
  onResult: (result: TranscriptionResult) => void;
  onError?: (err: Error) => void;
  onStatusChange?: (isActive: boolean) => void;
}

export class LiveTranscriptionService {
  private socket: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private processorNode: ScriptProcessorNode | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private isRunning: boolean = false;
  private targetLanguage: string = 'en';

  constructor(private readonly serviceUrl: string) {}

  public getIsRunning(): boolean {
    return this.isRunning;
  }

  public start(
    mediaStream: MediaStream,
    sessionId: string,
    handlers: LiveTranscriptionHandlers,
    targetLang: string = 'en'
  ): void {
    if (this.isRunning) return;
    this.targetLanguage = targetLang;

    // Conectar WebSocket hacia el microservicio Faster-Whisper
    const wsUrl = `${this.serviceUrl}?sessionId=${encodeURIComponent(sessionId)}&targetLang=${encodeURIComponent(targetLang)}&role=CITIZEN`;
    
    try {
      this.socket = new WebSocket(wsUrl);
      this.socket.binaryType = 'arraybuffer';

      this.socket.onopen = () => {
        this.isRunning = true;
        handlers.onStatusChange?.(true);
        this.setupAudioCapture(mediaStream, handlers);
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data && data.originalText) {
            handlers.onResult({
              sessionId: data.sessionId || sessionId,
              originalText: data.originalText,
              detectedLanguage: data.detectedLanguage || 'es',
              languageProbability: data.languageProbability,
              translatedText: data.translatedText,
              targetLanguage: this.targetLanguage,
              processingTimeMs: data.processingTimeMs,
              timestamp: data.timestamp || Date.now()
            });
          }
        } catch (e) {
          console.warn('[TRANSCRIPTION_WS] Error parseando mensaje:', e);
        }
      };

      this.socket.onerror = (err) => {
        console.warn('[TRANSCRIPTION_WS] Error en socket:', err);
        handlers.onError?.(new Error('Error de conexión con el servicio de transcripción'));
      };

      this.socket.onclose = () => {
        this.stop();
        handlers.onStatusChange?.(false);
      };
    } catch (err) {
      console.error('[TRANSCRIPTION] Falla iniciando conexión:', err);
      handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
      this.stop();
    }
  }

  private setupAudioCapture(mediaStream: MediaStream, handlers: LiveTranscriptionHandlers): void {
    try {
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new AudioContextClass();

      // Verificar que el stream tenga tracks de audio
      const audioTracks = mediaStream.getAudioTracks();
      if (!audioTracks || audioTracks.length === 0) {
        throw new Error('No se detectaron pistas de audio en el stream de la llamada.');
      }

      this.mediaStreamSource = this.audioContext.createMediaStreamSource(mediaStream);

      // Buffer size de 4096 muestras (~93ms a 44.1kHz / ~85ms a 48kHz)
      this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1);

      this.processorNode.onaudioprocess = (e) => {
        if (!this.isRunning || !this.socket || this.socket.readyState !== WebSocket.OPEN) return;

        const inputChannelData = e.inputBuffer.getChannelData(0);
        const sourceSampleRate = this.audioContext?.sampleRate || 48000;
        const targetSampleRate = 16000;

        // Re-muestreo a 16000 Hz si la tasa del navegador es superior
        const downsampled = this.downsampleBuffer(inputChannelData, sourceSampleRate, targetSampleRate);
        if (!downsampled || downsampled.length === 0) return;

        // Conversión Float32 a Int16 PCM (Little Endian)
        const pcmBuffer = new ArrayBuffer(downsampled.length * 2);
        const pcmView = new DataView(pcmBuffer);
        for (let i = 0; i < downsampled.length; i++) {
          const sample = downsampled[i] ?? 0;
          const s = Math.max(-1, Math.min(1, sample));
          const intVal = s < 0 ? s * 0x8000 : s * 0x7fff;
          pcmView.setInt16(i * 2, intVal, true);
        }

        // Enviar audio PCM crudo en paquetes binarios
        this.socket.send(pcmBuffer);
      };

      this.mediaStreamSource.connect(this.processorNode);
      this.processorNode.connect(this.audioContext.destination);
    } catch (err) {
      console.error('[AUDIO_CAPTURE] Error inicializando captura Web Audio:', err);
      handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  private downsampleBuffer(buffer: Float32Array, inputRate: number, outputRate: number): Float32Array {
    if (inputRate === outputRate) return buffer;
    if (inputRate < outputRate) return buffer;

    const ratio = inputRate / outputRate;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
      const nextIndex = Math.floor(i * ratio);
      result[i] = buffer[nextIndex] ?? 0;
    }
    return result;
  }

  public stop(): void {
    this.isRunning = false;

    if (this.processorNode) {
      this.processorNode.disconnect();
      this.processorNode = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN) {
        this.socket.close();
      }
      this.socket = null;
    }
  }
}
